import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { useMemo, useRef } from "react";

import { ISourceDataStatus } from "../../../../../types/baseRateFile";

const DATA_SET_LABELS: Record<keyof ISourceDataStatus, string> = {
  addendumB: "Addendum B",
  addendumAA: "Addendum AA",
  clfs: "CLFS",
  asp: "ASP",
  dmepos: "DMEPOS",
  pprvu: "MPFS (PPRRVU)",
};

const FILE_TYPE_OPTIONS = Object.entries(DATA_SET_LABELS).map(
  ([value, label]) => ({
    label,
    value,
  })
);

const getYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years: { label: string; value: number }[] = [];
  // Next year + current year + 4 previous years = 6 years total
  for (let i = currentYear + 1; i >= currentYear - 4; i -= 1) {
    years.push({ label: String(i), value: i });
  }
  return years;
};

export interface IFileUploadEntry {
  id: string;
  year: number;
  fileType: keyof ISourceDataStatus | null;
  file: File | null;
}

interface IFileUploadEntryComponentProps {
  entry: IFileUploadEntry;
  onChange: (entry: IFileUploadEntry) => void;
  onRemove: () => void;
  showRemove: boolean;
}

function FileUploadEntryComponent({
  entry,
  onChange,
  onRemove,
  showRemove,
}: IFileUploadEntryComponentProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const yearOptions = useMemo(() => getYearOptions(), []);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const { files } = e.dataTransfer;
    if (files && files.length > 0) {
      const file = files[0];
      const allowedExtensions = [".csv", ".xlsx", ".txt"];
      const hasValidExtension = allowedExtensions.some((ext) =>
        file.name.toLowerCase().endsWith(ext)
      );
      if (hasValidExtension) {
        onChange({ ...entry, file });
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      onChange({ ...entry, file: e.target.files[0] });
      e.target.value = "";
    }
  };

  const handleRemoveFile = () => {
    onChange({ ...entry, file: null });
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "0.5rem",
        padding: "1rem",
        border: "1px solid var(--surface-border)",
        borderRadius: "8px",
        position: "relative",
      }}
    >
      {showRemove && (
        <Button
          icon="pi pi-trash"
          className="p-button-text  p-button-sm text-red-500"
          onClick={onRemove}
          aria-label="Remove entry"
          style={{ position: "absolute", top: "0.2rem", right: "0.5rem" }}
        />
      )}

      {/* Year and File Type dropdowns side by side */}
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <div className="flex flex-column gap-2" style={{ flex: 1 }}>
          <label htmlFor={`year-select-${entry.id}`}>Year</label>
          <Dropdown
            id={`year-select-${entry.id}`}
            value={entry.year}
            options={yearOptions}
            onChange={(e) => onChange({ ...entry, year: e.value })}
            placeholder="Select Year"
            className="w-full"
          />
        </div>
        <div className="flex flex-column gap-2" style={{ flex: 1 }}>
          <label htmlFor={`file-type-select-${entry.id}`}>File Type</label>
          <Dropdown
            id={`file-type-select-${entry.id}`}
            value={entry.fileType}
            options={FILE_TYPE_OPTIONS}
            onChange={(e) => onChange({ ...entry, fileType: e.value })}
            placeholder="Select File Type"
            className="w-full"
          />
        </div>
      </div>

      {/* File upload area */}
      <input
        type="file"
        accept=".csv,.xlsx,.txt"
        onChange={handleFileSelect}
        style={{ display: "none" }}
        ref={fileInputRef}
      />
      {!entry.file ? (
        <div
          className="upload-area"
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
          onDragOver={handleDragOver}
          onDragEnter={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          role="button"
          tabIndex={0}
          aria-label="Upload file"
        >
          <i className="pi pi-upload" />
          <div className="flex flex-column gap-1">
            <p className="text-sm text-bluegray-600 font-semibold m-0">
              Drag and drop your file here
            </p>
            <p className="text-xs m-0" style={{ color: "#706f7b" }}>
              or click to browse (CSV, XLSX, TXT)
            </p>
          </div>
        </div>
      ) : (
        <div className="selected-file">
          <div className="file-info">
            <i className="pi pi-file" />
            <span className="file-name">{entry.file.name}</span>
            <span className="file-size">
              {(entry.file.size / 1024).toFixed(1)} KB
            </span>
          </div>
          <Button
            icon="pi pi-times"
            className="p-button-text p-button-plain p-button-sm"
            onClick={handleRemoveFile}
            aria-label="Remove file"
          />
        </div>
      )}
    </div>
  );
}

export default FileUploadEntryComponent;
